import * as E from '@react-email/components'
import { type MetaFunction } from '@remix-run/node'
import { Link, Outlet } from '@remix-run/react'
import {
	EmailPrimaryButtonLink,
	EmailSecondaryButtonLink,
	EmailTemplate,
} from '#app/components/email.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.js'
import { SiteLogo } from '#app/components/site-logo.js'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.js'
import {
	SITE_FULL_TITLE,
	SITE_BRAND_NAME,
} from '#app/utils/client-constants.js'

export async function loader() {
	return {}
}

export default function Index() {
	return (
		<main className="font-poppins">
			<div className="flex w-full items-center justify-between pb-8 pt-32 max-sm:flex-col max-sm:gap-32 max-sm:py-16 sm:gap-8">
				<div className="flex max-w-xl flex-col gap-2 sm:-mt-8">
					<SiteLogo showTagline={true} titleSize="text-7xl" />
				</div>
				<div className="flex justify-center">
					<div className="flex max-w-3xl flex-col justify-center gap-1">
						<Button
							className="text-primary-50 max-sm:w-[272px] sm:w-72"
							size={'xl'}
							asChild
						>
							<Link to="rankings" prefetch="intent" className="mb-4 flex gap-2">
								<Icon name="laptop">See rankings</Icon>
							</Link>
						</Button>
						<Button variant={'link'} size={'xl'} asChild>
							<Link
								to="/products/add-product"
								prefetch="intent"
								className="flex gap-2"
							>
								<Icon name="plus">Add product</Icon>
							</Link>
						</Button>
					</div>
				</div>
			</div>
			<Outlet />
		</main>
	)
}

export function NewsletterEmail({
	signupUrl,
	rankingsUrl,
}: {
	signupUrl: string
	rankingsUrl: string
}) {
	return (
		<EmailTemplate>
			<E.Container>
				<E.Heading>Welcome to {SITE_FULL_TITLE}</E.Heading>
				<p>
					<E.Text>
						Thank you for signing up for our newsletter! Every once in a while
						you'll receive the latest and greatest products.
					</E.Text>
				</p>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						width: '100%',
					}}
				>
					<EmailPrimaryButtonLink href={rankingsUrl}>
						See Rankings
					</EmailPrimaryButtonLink>
					<EmailSecondaryButtonLink href={signupUrl}>
						Create Account
					</EmailSecondaryButtonLink>
				</div>
				<p>
					<E.Text>
						We're thrilled to have you with us! Stay tuned for your first
						newsletter.
					</E.Text>
				</p>
				<E.Text>
					All the best,
					<br />
					The {SITE_BRAND_NAME} Team
				</E.Text>
			</E.Container>
		</EmailTemplate>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}

export const meta: MetaFunction<typeof loader> = () => {
	return [
		{
			title: `Independent Reviews of Products | ${SITE_FULL_TITLE}`,
		},
		{
			name: 'description',
			content: `${SITE_FULL_TITLE} helps you discover, compare, and share the best products. Read unbiased reviews from real users to make informed decisions and explore top-rated items.`,
		},
	]
}
